'use client';

import { useEffect } from 'react';

// ----------------------------------------------------------------------

/**
 * Safety net for a tab whose lazy-loaded chunks no longer exist.
 *
 * Deploys keep the previous builds' hashed chunks in S3 for a grace period,
 * so this should not happen — but a tab left open past that window (or a
 * user who ignored the update prompt for days) can still request a chunk
 * that has been garbage-collected. CloudFront answers missing paths with
 * `index.html`, so the browser gets HTML where JS is expected and the app
 * white-screens. Reloading fetches the current build and recovers.
 *
 * A session-scoped flag prevents a reload loop: if the reloaded page fails
 * the same way within the healthy-boot window, the error is left to surface
 * instead of reloading forever. The flag is cleared once the boot proves
 * healthy, so a later failure in the same tab can still recover.
 */
const RELOAD_GUARD_KEY = 'chunk-load-recovery:reloaded';

/**
 * How long a boot must stay healthy before the one-shot reload guard is
 * cleared. A reload loop manifests immediately — the page reloads and fails
 * again within seconds — so surviving this window means we recovered cleanly
 * and the safety net can be re-armed for a *later* failure (e.g. a second
 * deploy in the same long-lived tab).
 */
const HEALTHY_BOOT_MS = 10_000;

/**
 * Genuine webpack/Next chunk-loading failures. Matched by name or message,
 * so it works for both `error` events and `unhandledrejection` reasons.
 *
 * Note: a bare "Unexpected token '<'" is deliberately NOT matched here. That
 * SyntaxError is also thrown by `response.json()` / `JSON.parse` on an HTML
 * error page (e.g. an API 5xx), and matching it would reload — discarding
 * unsaved edits — on an ordinary API failure. The HTML-as-JS chunk case is
 * handled separately by `isHtmlServedAsChunk`, scoped to script URLs.
 */
function isChunkLoadError(value: unknown): boolean {
  if (!value) return false;

  const { name, message } = value as { name?: unknown; message?: unknown };

  if (name === 'ChunkLoadError') return true;

  return (
    typeof message === 'string' &&
    (/Loading chunk [^\s]+ failed/i.test(message) ||
      /Loading CSS chunk [^\s]+ failed/i.test(message) ||
      /importing a module script failed/i.test(message))
  );
}

/**
 * The white-screen signature: a `<script>` whose source is a hashed chunk was
 * served `index.html` (SPA fallback) and failed to parse as JavaScript. This
 * fires a window `error` event whose `filename` is the script URL. Scoping to
 * `/_next/static/` is what makes it safe — a `JSON.parse` failure elsewhere
 * has no such script filename, so it can never be mistaken for a bad chunk.
 */
function isHtmlServedAsChunk(event: ErrorEvent): boolean {
  const filename = typeof event.filename === 'string' ? event.filename : '';
  if (!/\/_next\/static\//.test(filename)) return false;

  const errorMessage =
    typeof (event.error as { message?: unknown })?.message === 'string'
      ? (event.error as { message: string }).message
      : '';
  const message = typeof event.message === 'string' ? event.message : '';
  const combined = `${errorMessage} ${message}`;

  // HTML parsed as a script trips on the leading "<" of "<!DOCTYPE html>".
  // Phrasing differs across engines (Chrome/Safari vs Firefox).
  return /Unexpected token '<'/i.test(combined) || /expected expression, got '<'/i.test(combined);
}

function reloadOnce() {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  } catch {
    // Storage blocked (private mode / strict settings): without the guard a
    // reload could loop, so do nothing and let the error surface.
    return;
  }

  window.location.reload();
}

export function useChunkLoadRecovery(): void {
  useEffect(() => {
    // Re-arm the safety net once this boot proves healthy. If the reload we
    // just performed had failed the same way (a loop), the failure would have
    // fired again before this fires, and the guard would have suppressed it.
    const rearmTimer = window.setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
      } catch {
        // Storage blocked — nothing to clear.
      }
    }, HEALTHY_BOOT_MS);

    const onError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error) || isChunkLoadError(event) || isHtmlServedAsChunk(event)) {
        reloadOnce();
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Only genuine chunk-load rejections — never a bare JSON parse error,
      // which would reload and discard unsaved work on an ordinary API 5xx.
      if (isChunkLoadError(event.reason)) {
        reloadOnce();
      }
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.clearTimeout(rearmTimer);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);
}

// ----------------------------------------------------------------------

/** Exported for tests. */
export const chunkLoadRecoveryInternals = {
  isChunkLoadError,
  isHtmlServedAsChunk,
  RELOAD_GUARD_KEY,
  HEALTHY_BOOT_MS,
};
